import {Component, inject, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatTabGroup} from '@angular/material/tabs';
import {MatDialog} from '@angular/material/dialog';
import {WorkflowsService} from '../../../../services/objects/workflows.service';
import {ToastService} from '../../../../services/layout/toast.service';
import {ConfirmDialogComponent} from '../../../confirm-dialog/confirm-dialog.component';
import {MaterialModule} from "../../../../material.module";

import {IWorkflowOptions, IWorkflowResponse, IWorkflowStepOptions} from "../../../../models/workflow-options.model";
import {WorkflowReportComponent} from "../../reports/workflow-report.component";
import {TabbedPageComponent} from "../../../objects/tabbed-page/tabbed-page.component";
import {ScheduleSettingsComponent} from "../../../objects/scheduling/schedule-settings.component";
import {interval, takeWhile} from "rxjs";
import {switchMap} from "rxjs/operators";
import {ContentInterpreterService} from "../../../../services/content-interpreter.service";
import {ClientObjectSettings} from "../../../objects/client-object-settings/client-object-settings.component";
import {NotificationSettingsComponent} from "../../../objects/notifications/notification-settings.component";
import {WorkflowPreviousRunsComponent} from "../workflow-previous-runs/workflow-previous-runs.component";

@Component({
  selector: 'app-workflow-settings',
  templateUrl: './workflow-settings.component.html',
  styleUrls: ['./workflow-settings.component.scss'],
    imports: [MaterialModule, ReactiveFormsModule, FormsModule, WorkflowReportComponent, ScheduleSettingsComponent, ClientObjectSettings, NotificationSettingsComponent, WorkflowPreviousRunsComponent]
})
export class WorkflowSettingsComponent extends TabbedPageComponent implements OnInit{
    private fb = inject(FormBuilder);
    private workflowsService = inject(WorkflowsService);
    private toastService = inject(ToastService);
    private dialog = inject(MatDialog);
    private contentInterpreter = inject(ContentInterpreterService);


  @ViewChild('matTabGroup') matTabGroup!: MatTabGroup;

  isSaving: boolean = false;
  isDeleting: boolean = false;
  isRunning: boolean = false;

  runningWorkflow: any;

  workflowOptions: IWorkflowOptions | null = null;
  id: string = '';

  // Reactive Forms
  scheduleSettingsForm: FormGroup;
  clientOptionsSettingsForm: FormGroup;
  notificationsForm: FormGroup;
  stepForms: FormGroup[] = [];

  reportData: any[];

  // Run Results
  results: IWorkflowResponse[] = [];

  // Parameters for adding properties
  keyToAdd: { [key: number]: string } = {};
  valueToAdd: { [key: number]: string } = {};

  // Allowed options (assumed to be provided by service or constants)
  allowedStepTypes: string[] = ['Selection', 'Transformation', 'Analysis', 'Generation', 'Evaluation', 'Image'/*, 'Notification'*/];
  allowedActionTypes: string[] = ['Replace', 'Prepend', 'Append'];
  allowedParameters: {
    [key: string]: Array<{ name: string, description: string, type: string, options?: string[] }> } = {
    'Selection': [
      { name: 'url', description: 'Endpoint URL', type: 'url', options: [] },
      { name: 'limit', description: 'Limit', type: 'number', options: [] },
      { name: 'crawlType', description: 'Type of Crawl', type: 'select', options: ['Url', 'Json', 'Xml'] }
    ],
    'Transformation': [
      { name: 'Type', description: 'Type of transformation', type: 'text', options: ['HtmlParseSinglePropertyTransformer', 'HtmlParseFromAttributeTransformer', 'HtmlParseInnerTextTransformer', 'HtmlParseMinimisedTransformer', 'HtmlToMarkdownTransformer', 'HtmlParseFromHtmlTableTransformer', 'HtmlParseFromHtmlListTransformer', 'HtmlToSchemaOrgJsonLdTransformer', 'GoogleMerchantXmlTransformer'] },
      { name: 'Transformer', description: 'Transformation', type: 'text', options: ['HtmlParseSinglePropertyTransformer', 'HtmlParseFromAttributeTransformer', 'HtmlParseInnerTextTransformer', 'HtmlParseMinimisedTransformer', 'HtmlToMarkdownTransformer', 'HtmlParseFromHtmlTableTransformer', 'HtmlParseFromHtmlListTransformer', 'HtmlToSchemaOrgJsonLdTransformer', 'GoogleMerchantXmlTransformer'] },
      { name: 'PrimaryNode', description: 'Primary Node', type: 'text', options: [] },
      { name: 'Name', description: 'Name', type: 'text', options: [] }
    ],
    'Analysis': [
      { name: 'OutputFormat', description: 'Json structure', type: 'text', options: [] },
      { name: 'Prompt', description: 'Instructions', type: 'text', options: [] }
    ],
    'Generation': [
      { name: 'OutputFormat', description: 'Json structure', type: 'text', options: [] },
      { name: 'Prompt', description: 'Instructions', type: 'text', options: [] }
    ],
    'Evaluation': [
      { name: 'OutputFormat', description: 'Json structure', type: 'text', options: [] },
      { name: 'Prompt', description: 'Instructions', type: 'text', options: [] }
    ],
    'Image': [
      { name: 'OutputFormat', description: 'Json structure', type: 'text', options: [] },
      { name: 'Prompt', description: 'Instructions', type: 'text', options: [] }
    ],

  };
  allTasks: any[];


  ngOnInit(): void {
    super.OnInit()
    this.clientOptionsSettingsForm = this.fb.group({});
    this.scheduleSettingsForm = this.fb.group({});
    this.notificationsForm = this.fb.group({});
    this.route.params.subscribe(params => {
      this.id = params['id'];
      this.loadData();
    });
  }

  async loadData() {
    try {
      this.workflowOptions = await this.workflowsService.getByIdAsync(this.id).toPromise() || {} as IWorkflowOptions;
      const allTasks = await this.workflowsService.getAllTasksAsync(this.id).toPromise();
      this.allTasks = allTasks as any[]
      // Populate Settings Form

      // Initialize Step Forms
      this.workflowOptions.steps.forEach((step) => {
        const stepForm = this.fb.group({
          name: [step.name, Validators.required],
          stepType: [step.stepType, Validators.required],
          actionType: [step.actionType, Validators.required]
        });
        this.stepForms.push(stepForm);
      });

      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 7);
      const endDate = new Date();

      this.workflowsService.reportAsync(this.workflowOptions?.id ?? "", startDate.toISOString(), endDate.toISOString()).subscribe(data => {
        this.reportData = data;
      });

    } catch (error) {
      console.error('Error loading workflow data', error);
      this.toastService.showError('Error loading workflow data', 3000);
    }
  }

  // Save Workflow
  save() {
    if (this.scheduleSettingsForm.invalid) {
      this.toastService.showError('Please fix the errors in the form before saving.', 3000);
      return;
    }

    this.isSaving = true;

    // Update workflowOptions with form values
    Object.assign(this.workflowOptions!, this.clientOptionsSettingsForm.value);
    this.workflowOptions!.scheduleSettings = this.scheduleSettingsForm.value;
    this.workflowOptions!.notificationSettings = this.notificationsForm.value;

    // Update steps
    this.workflowOptions!.steps.forEach((step, index) => {
      step.name = this.stepForms[index].value.name;
      step.stepType = this.stepForms[index].value.stepType;
      step.actionType = this.stepForms[index].value.actionType;
      // Parameters are already managed via addProperty/deleteProperty
    });

    // Save via service
    this.workflowsService.saveAsync(this.workflowOptions!).subscribe(() => {
      this.toastService.showSuccess('Settings saved successfully', 3000);
      this.isSaving = false;
    }, error => {
      console.error('Error saving workflow settings', error);
      this.toastService.showError('Error saving workflow settings', 3000);
      this.isSaving = false;
    });
  }

  // Delete Workflow
  delete() {
    if (!this.workflowOptions) return;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '350px',
      data: {
        title: 'Are you sure you want to delete this function?',
        message: 'This will delete the function record. Once deleted, it cannot be rolled back.',
        confirmText: 'Do you want to proceed?',
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel'
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isDeleting = true;
        this.toastService.showInfo('Deleting workflow...', 3000);
        this.workflowsService.deleteAsync(this.workflowOptions!.id).subscribe(() => {
          this.toastService.showSuccess('Workflow deleted successfully.', 3000);
          this.isDeleting = false;
          this.router.navigate(['/ai/workflows']);
        }, error => {
          console.error('Error deleting workflow', error);
          this.toastService.showError('Error deleting workflow.', 3000);
          this.isDeleting = false;
        });
      } else {
        this.toastService.showInfo('Delete action canceled.', 3000);
      }
    });
  }



  // Download Excel
  downloadExcel() {
    if (!this.workflowOptions || !this.results) return;

    this.toastService.showInfo('Generating Excel file...', 3000);
    this.workflowsService.downloadExcelFileForWorkflowAsync(this.results).subscribe((file: Blob) => {
      const fileName = `${this.workflowOptions?.name ?? "error"}.xlsx`;
      const link = document.createElement('a');
      const url = window.URL.createObjectURL(file);
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);
      this.toastService.showSuccess('Excel file downloaded successfully.', 3000);
    }, error => {
      console.error('Error downloading Excel file', error);
      this.toastService.showError('Error downloading Excel file.', 3000);
    });
  }

  // Add Step
  addStep() {
    if (!this.workflowOptions) return;

    this.scheduleSettingsForm.markAsTouched();
    const newStep = new IWorkflowStepOptions();
    // create new guid for step
    newStep.stepId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
    newStep.name = 'New Step';
    newStep.stepType = '';
    newStep.actionType = '';

    this.workflowOptions.steps.push(newStep);

    const stepForm = this.fb.group({
      name: [newStep.name, Validators.required],
      stepType: [newStep.stepType, Validators.required],
      actionType: [newStep.actionType, Validators.required]
    });
    this.stepForms.push(stepForm);
  }

  // Remove Step
  removeStep(index: number) {
    this.scheduleSettingsForm.markAsTouched();
    this.workflowOptions?.steps.splice(index, 1);
    this.stepForms.splice(index, 1);
    delete this.keyToAdd[index];
    delete this.valueToAdd[index];
  }

  // Add Property to Step
  addProperty(stepIndex: number) {
    this.scheduleSettingsForm.markAsTouched();
    const key = this.keyToAdd[stepIndex];
    const value = this.valueToAdd[stepIndex];

    if (!key || !value) {
      this.toastService.showError('Key and Value are required to add a property.', 3000);
      return;
    }

    const step = this.workflowOptions?.steps[stepIndex];
    if (step) {
      step.parameters[key] = value;
      this.keyToAdd[stepIndex] = '';
      this.valueToAdd[stepIndex] = '';
    }
  }

  // Delete Property from Step
  deleteProperty(stepIndex: number, key: string) {
    this.scheduleSettingsForm.markAsTouched();
    const step = this.workflowOptions?.steps[stepIndex];
    if (step && step.parameters[key]) {
      delete step.parameters[key];
    }
  }

  // Helper Methods for Parameters
  hasOptions(stepType: string, key: string): boolean {
    const param = this.allowedParameters[stepType]?.find(p => p.name === key);
    return !!param?.options && param.options.length > 0;
  }

  getType(stepType: string, key: string): string {
    const param = this.allowedParameters[stepType]?.find(p => p.name === key);
    return param?.type ?? 'text';
  }


  getOptions(stepType: string, key: string): string[] {
    const param = this.allowedParameters[stepType]?.find(p => p.name === key);
    return param?.options || [];
  }

  getParameters(step: IWorkflowStepOptions) {
    return Object.keys(step.parameters).map(key => ({ key, value: step.parameters[key] }));
  }

  getStepColumns(): string[] {
    const displayColumns = ['url'];

    const result = this.results[0];
    for (let i = 0; i < result.Steps.length; i++) {
      const stepName = Object.keys(result.Steps[i].Result)[0];
      displayColumns.push('step' + i + '_' + stepName);
    }
    return displayColumns;
  }

  isFormTouched(): boolean {
    if (this.scheduleSettingsForm.touched) return true;
    if (this.notificationsForm.touched) return true;
    if (this.clientOptionsSettingsForm.touched) return true;
    return this.stepForms.some(form => form.touched);
  }

  getStepColumnName(index: number): string {
    console.log(this.results[0].Steps[index].Result);
    const toReturn = "step" + index + "_" + Object.keys(this.results[0].Steps[index].Result)[0];
    console.log(toReturn);
    return toReturn;
  }

  getStepName(index: number): string {
    return Object.keys(this.results[0].Steps[index].Result)[0];
  }

  runWorkflow() {
    this.toastService.showInfo('Running workflow', 3000);
    this.isRunning = true;

    this.workflowsService.runWorkflowAsync(this.id)
        .subscribe(initialTask => {
          // Store the initial task
          this.runningWorkflow = initialTask;

          // Create a polling observable that fires every 3 seconds
          const polling$ = interval(5000).pipe(
              switchMap(() => this.workflowsService.getTaskAsync(this.id, this.runningWorkflow.id)),
              // Continue polling until CompletedSuccessfully is true.
              takeWhile(task => !task.completedSuccessfully, true)
          );

          polling$.subscribe(updatedTask => {
            this.runningWorkflow = updatedTask;
            if (updatedTask.completedSuccessfully) {
              if (updatedTask.taskExecutionResult &&
                  updatedTask.taskExecutionResult.outputs &&
                  updatedTask.taskExecutionResult.outputs[0].base64Data &&
                  updatedTask.taskExecutionResult.outputs[0].contentType) {

                // Interpret the content generically.
                const interpretedResponse = this.contentInterpreter.interpretContent(
                    updatedTask.taskExecutionResult.outputs[0].contentType,
                    updatedTask.taskExecutionResult.outputs[0].base64Data
                );
                this.results = interpretedResponse as IWorkflowResponse[];
                console.log(this.results);
              }
              this.isRunning = false;
              this.toastService.showSuccess('Test run successfully', 3000);
            }
          });
        });
  }
}
